import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Checkbox,
  ActionIcon,
  Menu,
  Modal,
  Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Trash2, User, CheckCircle } from 'react-feather';
import { type CohortCoachWithDetails } from '../../../../services/api/cohortCoaches';
import { ConfigurableTable } from '../../../../components/common';
import { renderCoachCellContent } from './CoachCellRenderers';
import { COACH_DEFAULT_COLUMNS, getCoachColumnMinWidth } from './coachColumnConfig';

interface CoachesInCohortTableProps {
  coaches: CohortCoachWithDetails[];
  selectedCoaches: string[];
  onToggleSelection: (coachUserId: string) => void;
  onSelectAll: () => void;
  onRemoveCoach: (coachUserId: string, sendThankYou?: boolean) => void;
  onCoachClick: (coachUserId: string) => void;
  onMarkContractSigned?: (coachUserId: string) => void;
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  loading: boolean;
}

export function CoachesInCohortTable({
  coaches,
  selectedCoaches,
  onToggleSelection,
  onSelectAll,
  onRemoveCoach,
  onCoachClick,
  onMarkContractSigned,
  page,
  totalPages: _totalPages,
  totalCount,
  onPageChange,
  loading,
}: CoachesInCohortTableProps) {
  // Removal modal state (EC-35: Thank you email option)
  const [removeModalOpened, { open: openRemoveModal, close: closeRemoveModal }] = useDisclosure(false);
  const [coachToRemove, setCoachToRemove] = useState<CohortCoachWithDetails | null>(null);
  const [sendThankYou, setSendThankYou] = useState(false);

  const handleRemoveCoach = (coach: CohortCoachWithDetails) => {
    setCoachToRemove(coach);
    setSendThankYou(coach.contract_status !== 'pending');
    openRemoveModal();
  };

  const confirmRemoval = () => {
    if (coachToRemove) {
      onRemoveCoach(coachToRemove.user_id, sendThankYou);
      closeRemoveModal();
      setCoachToRemove(null);
      setSendThankYou(false);
    }
  };

  const getCoachName = (coach: CohortCoachWithDetails) => {
    const name = [coach.first_name, coach.last_name].filter(Boolean).join(' ');
    return name || coach.email;
  };

  // Sort coaches by first name for consistent ordering
  const sortedCoaches = [...coaches].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || ''),
  );

  const allSelected = coaches.length > 0 && coaches.every(c => selectedCoaches.includes(c.user_id));
  const someSelected = selectedCoaches.length > 0 && !allSelected;

  const renderActions = (coach: CohortCoachWithDetails) => (
    <Menu position="bottom-end" shadow="md">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <MoreHorizontal size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {/* MED-1355 QA round 5: the same tab. A second tab is a second copy
            of the app, and during impersonation it can end the session. */}
        <Menu.Item
          leftSection={<User size={14} />}
          component={Link}
          to={`/coaches/${coach.coach_profile_id}`}
        >
          View Profile
        </Menu.Item>
        {/* MED-685: partner coaches are excluded from contract tracking. */}
        {/* MED-1177: a contract can be marked received without first sending the
            announcement email, so the action shows for any not-yet-signed coach
            (pending or announced) — announcing is optional, not a prerequisite. */}
        {coach.coach_type !== 'partner' &&
          coach.contract_status !== 'signed' &&
          coach.assigned_groups &&
          coach.assigned_groups.length > 0 &&
          onMarkContractSigned && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={<CheckCircle size={14} />}
                onClick={() => onMarkContractSigned(coach.user_id)}
              >
                Contract signed and received
              </Menu.Item>
            </>
          )}
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<Trash2 size={14} />}
          onClick={() => handleRemoveCoach(coach)}
        >
          Remove from Cohort
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <>
      <ConfigurableTable<CohortCoachWithDetails>
        data={sortedCoaches}
        columns={COACH_DEFAULT_COLUMNS}
        getRowKey={(c) => c.id}
        renderCell={(coach, column) => renderCoachCellContent(column, coach)}
        renderActions={renderActions}
        sortConfig={null}
        onSort={() => {}}
        loading={loading}
        page={page}
        pageSize={25}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={() => {}}
        visibleColumnCount={COACH_DEFAULT_COLUMNS.length}
        emptyMessage="No coaches in this cohort"
        stickyFirstColumn
        stickyActions
        getColumnMinWidth={getCoachColumnMinWidth}
        getRowStyle={(coach) =>
          selectedCoaches.includes(coach.user_id)
            ? { backgroundColor: 'var(--mantine-color-medleyGreen-0)' }
            : undefined
        }
        onRowClick={(coach) => onCoachClick(coach.user_id)}
        renderLeadingHeader={() => (
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={onSelectAll}
          />
        )}
        renderLeadingCell={(coach) => (
          <Checkbox
            checked={selectedCoaches.includes(coach.user_id)}
            onChange={() => onToggleSelection(coach.user_id)}
          />
        )}
        entityLabel="coaches"
      />

      {/* Removal Confirmation Modal (EC-35) */}
      <Modal
        opened={removeModalOpened}
        onClose={closeRemoveModal}
        title="Remove Coach"
        size="sm"
      >
        {coachToRemove && (
          <Stack gap="md">
            <Text size="sm">
              Are you sure you want to remove <strong>{getCoachName(coachToRemove)}</strong> from this cohort?
              This will remove them from the potential coaches pool.
            </Text>

            {coachToRemove.contract_status !== 'pending' && (
              <Checkbox
                label="Send thank you email to coach"
                description="Coach will receive an email thanking them for their interest"
                checked={sendThankYou}
                onChange={(e) => setSendThankYou(e.currentTarget.checked)}
              />
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={closeRemoveModal}>
                Cancel
              </Button>
              <Button color="red" onClick={confirmRemoval}>
                Remove
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

export default CoachesInCohortTable;
